import React, { useState, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import { NavigationUtils } from 'lib/navigationUtilities';
import { AuthContext } from 'components/user/AuthContext';
import { ReferrerContext } from 'components/layout/ReferrerContext';
import Button from 'components/element/Button';
import Input from 'components/element/Input';
import FormContainer from 'components/layout/FormContainer';
import api from 'lib/api';

const RegisterForm = () => {
  const { handleLogin } = useContext(AuthContext);
  const { referrer } = useContext(ReferrerContext);
  const { navigateToLocation } = NavigationUtils();
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    bio: '',
    avatar: null,
    banner: null,
  });

  const registerMutation = useMutation({
    mutationFn: (data) => {
      const formDataToSend = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formDataToSend.append(key, data[key]);
        }
      });

      return api.auth.register(formDataToSend);
    },
    onSuccess: (data) => {
      setErrorMessage('');
      setFieldErrors({});
      handleLogin(data.token);
      navigateToLocation(referrer || '/');
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        const updatedErrors = {};

        Object.keys(error.response.data.errors).forEach((field) => {
          const fieldError = error.response.data.errors[field];
          updatedErrors[field] = Array.isArray(fieldError) ? fieldError[0] : fieldError;
        });

        setFieldErrors(updatedErrors);
      } else {
        setErrorMessage(
          error.response?.data?.message || error.message || 'Registration failed'
        );
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setErrorMessage('');
    registerMutation.mutate(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prev) => ({ ...prev, [name]: files[0] }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCloseAlert = () => {
    setErrorMessage('');
  };

  return (
    <FormContainer title="Register for an Account" errorMessage={errorMessage} onCloseAlert={handleCloseAlert}>
      <form onSubmit={handleSubmit} className="space-y-6">

        <div className="p-4 bg-gray-weak rounded-lg border border-gray-less space-y-4">
          <Input name="username" value={formData.username} onChange={handleChange} placeholder="Enter a username" label="Username" error={fieldErrors.username} className="w-full py-2.5" required />
          <Input name="name" value={formData.name} onChange={handleChange} placeholder="Enter your display name" label="Display Name" error={fieldErrors.name} className="w-full py-2.5" required />
          <Input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Enter your password" label="Password" error={fieldErrors.password} className="w-full py-2.5" required />
          <Input type="password" name="password_confirmation" value={formData.password_confirmation} onChange={handleChange} placeholder="Confirm your password" label="Password Confirmation" error={fieldErrors.password_confirmation} className="w-full py-2.5" required />

          <div className="p-4 bg-gray-less rounded-lg border border-gray-mid space-y-4">
            <p className="text-sm italic text-gray-strong">
              Enter at least one of the following:
            </p>
            <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email" label="Email Address" error={fieldErrors.email} className="w-full py-2.5" />
            <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" label="Phone Number" error={fieldErrors.phone} className="w-full py-2.5" />
          </div>
        </div>

        <div className="p-4 bg-gray-weak rounded-lg border border-gray-less space-y-4">
          <Input name="bio" value={formData.bio} onChange={handleChange} placeholder="Tell us about yourself" label="Bio" error={fieldErrors.bio} rows="3" className="w-full py-2.5" />
          <Input type="file" name="avatar" onChange={handleFileChange} label="Avatar (max 2MB)" error={fieldErrors.avatar} className="w-full py-2.5" accept="image/*" />
          <Input type="file" name="banner" onChange={handleFileChange} label="Banner (max 4MB)" error={fieldErrors.banner} className="w-full py-2.5" accept="image/*" />
        </div>

        <Button type="submit" isLoading={registerMutation.isPending} disabled={registerMutation.isPending} className="w-full py-2.5 text-white bg-blue-mid hover:bg-blue-more">
          Register
        </Button>
      </form>
    </FormContainer>
  );
};

export default RegisterForm;
